/* LA SELECTION D'UNE LIGNE DE REGLEMENT.

   Trois regles qui vivaient dans le JSX, et qu'on ne pouvait donc pas eprouver.

   La case a cocher etait le seul moyen de retenir un sinistre : une cible de quinze
   pixels dans un tableau qui en fait mille de large. Le clic porte desormais sur toute
   la ligne - ce qui fait apparaitre deux pieges que ce fichier existe pour tenir.

   PREMIER PIEGE : la double bascule. La case a cocher est dans la ligne. Un clic
   dessus declenche son propre `onChange` ET le `onClick` de la ligne : deux bascules,
   donc aucun effet, et une case qui refuse de se cocher. `vientDeLaCaseACocher` laisse
   la case faire son travail seule.

   SECOND PIEGE : deux regles de disponibilite. La case etait desactivee par
   `getCheckboxProps` ; si le clic de ligne portait sa propre condition, les deux
   auraient fini par differer - et l'on aurait pu retenir, en cliquant la ligne, un
   sinistre que la case refusait. `nonSelectionnable` est la seule regle. */

/**
 * Un sinistre ne peut pas etre retenu s'il est deja paye, ou si les fonds disponibles
 * ne couvrent pas son montant TTC.
 *
 * @param {object} record       la ligne du tableau
 * @param {object} lignes       les montants saisis, par tracking id de sinistre
 * @param {number} tauxTva      0 pour les reglements aux experts (decision metier)
 */
export function nonSelectionnable(record, lignes, tauxTva) {
  if (!record) return true;
  if (record.dejaPaye) return true;
  const dispo = Number(record.fondsDisponibles ?? 0);
  const ligne = lignes?.[record.sinistreTrackingId];
  const ht = Number(ligne?.montantHt ?? record.montantHtPropose ?? 0);
  const ttc = ht * (1 + tauxTva);
  return dispo < ttc;
}

/** Ajoute ou retire une cle, sans jamais la dupliquer. */
export function basculer(cles, cle) {
  const actuelles = cles || [];
  return actuelles.includes(cle)
    ? actuelles.filter((k) => k !== cle)
    : [...actuelles, cle];
}

/**
 * Les elements d'une ligne qui font leur propre travail, et dont le clic ne doit donc
 * pas basculer la selection.
 *
 * <p>`ant-table-selection-column` est la cellule ou AntD place la case a cocher.
 * `[data-sans-bascule]` est la marque que porte tout autre controle pose dans une
 * ligne - aujourd'hui le bouton qui ouvre le tiroir des details.
 */
const SELECTEURS_NEUTRES = ['.ant-table-selection-column', '[data-sans-bascule]'];

/**
 * Le clic doit-il etre laisse a l'element clique, sans basculer la ligne ?
 *
 * <p>Sans cela, un clic sur la case a cocher declencherait son propre `onChange` ET le
 * `onClick` de la ligne : deux bascules, donc aucun effet. Un clic sur le bouton des
 * details cocherait le sinistre au passage, ce que personne n'a demande.
 *
 * @param {{closest?: Function}} cible l'element clique (`event.target`)
 */
export function clicANePasBasculer(cible) {
  if (!cible || typeof cible.closest !== 'function') return false;
  return SELECTEURS_NEUTRES.some((sel) => Boolean(cible.closest(sel)));
}

/**
 * Partage les sinistres d'un expert entre ceux qui restent a payer et ceux qui l'ont
 * deja ete.
 *
 * <p>POURQUOI CE PARTAGE EXISTE. Le serveur rend les deux ensemble, chaque ligne portant
 * son `dejaPaye`, et l'ecran les melait : les lignes payees etaient grisees et
 * impossibles a cocher, mais elles occupaient le tableau et faussaient le compte. Sur un
 * expert de longue date, elles finissent par etre la majorite - on cherchait les lignes
 * a traiter parmi celles qui ne le sont plus.
 *
 * <p>Le partage est client, et c'est voulu : la reponse porte deja les deux, et un second
 * appel pour obtenir ce qu'on tient deja serait un aller-retour pour rien.
 */
export function partagerParPaiement(sinistres) {
  const tous = sinistres || [];
  return {
    payables: tous.filter((s) => !s?.dejaPaye),
    payes: tous.filter((s) => Boolean(s?.dejaPaye)),
  };
}
